import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDate } from '../../../utils/stringUtils';
import {
  ArrowLeft,
  Download,
  User,
  Activity,
  Shield,
  FileText,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Database,
  Settings,
  Lock,
  Edit,
  Trash2,
  Plus,
  Monitor,
  Smartphone,
  Globe,
  MapPin,
  Eye
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { auditLogService } from '../../../api/services/auditLogService';

const AuditLogDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [auditLog, setAuditLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedLogs, setRelatedLogs] = useState([]);

  useEffect(() => {
    loadAuditLog();
    loadRelatedLogs();
  }, [id]);

  const loadAuditLog = async () => {
    try {
      setLoading(true);
      const resp = await auditLogService.getAuditLogById(id);
      const payload = resp?.data || resp;
      const log = payload || {};
      setAuditLog({
        id: log.id,
        timestamp: log.timestamp,
        user: log.userName || log.user || 'Unknown',
        action: log.action,
        module: log.module,
        status: log.status,
        riskLevel: log.riskLevel,
        ipAddress: log.ipAddress,
        location: log.location || '',
        deviceType: log.deviceType || '',
        userAgent: log.userAgent || '',
        details: log.details || {},
        sessionId: log.sessionId || '',
        requestId: log.requestId || '',
        responseTime: log.responseTime || '',
        additionalContext: log.metadata || log.additionalContext || {}
      });
    } catch (e) {
      setAuditLog(null);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedLogs = () => {
    // Mock related logs - in real app, fetch related audit logs
    const mockRelatedLogs = [
      {
        id: 'AUDIT-002',
        timestamp: '2024-12-10T09:45:00Z',
        user: 'john.doe@company.com',
        action: 'LOGIN',
        module: 'Authentication',
        status: 'SUCCESS',
        riskLevel: 'LOW'
      },
      {
        id: 'AUDIT-003',
        timestamp: '2024-12-10T08:20:00Z',
        user: 'john.doe@company.com',
        action: 'VIEW',
        module: 'Employee Database',
        status: 'SUCCESS',
        riskLevel: 'LOW'
      },
      {
        id: 'AUDIT-004',
        timestamp: '2024-12-09T16:30:00Z',
        user: 'john.doe@company.com',
        action: 'UPDATE',
        module: 'Employee Database',
        status: 'SUCCESS',
        riskLevel: 'LOW'
      }
    ];
    
    setRelatedLogs(mockRelatedLogs);
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'LOGIN':
      case 'LOGOUT': return <User className="h-5 w-5" />;
      case 'CREATE': return <Plus className="h-5 w-5" />;
      case 'UPDATE': return <Edit className="h-5 w-5" />;
      case 'DELETE': return <Trash2 className="h-5 w-5" />;
      case 'EXPORT': return <Download className="h-5 w-5" />;
      case 'BACKUP': return <Database className="h-5 w-5" />;
      case 'ACCESS_DENIED': return <Lock className="h-5 w-5" />;
      case 'PASSWORD_CHANGE': return <Shield className="h-5 w-5" />;
      case 'VIEW': return <Eye className="h-5 w-5" />;
      default: return <Activity className="h-5 w-5" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS': return 'bg-green-100 text-green-800';
      case 'FAILED': return 'bg-red-100 text-red-800';
      case 'PENDING': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'HIGH': return 'bg-red-100 text-red-800';
      case 'MEDIUM': return 'bg-yellow-100 text-yellow-800';
      case 'LOW': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDeviceIcon = (deviceType) => {
    switch (deviceType) {
      case 'Desktop': return <Monitor className="h-4 w-4" />;
      case 'Mobile': return <Smartphone className="h-4 w-4" />;
      case 'Tablet': return <Monitor className="h-4 w-4" />;
      default: return <Globe className="h-4 w-4" />;
    }
  };

  // formatDate is now imported from stringUtils

  const handleExportLog = () => {
    if (!auditLog) return;

    const logData = {
      id: auditLog.id,
      timestamp: auditLog.timestamp,
      user: auditLog.user,
      action: auditLog.action,
      module: auditLog.module,
      status: auditLog.status,
      riskLevel: auditLog.riskLevel,
      ipAddress: auditLog.ipAddress,
      location: auditLog.location,
      deviceType: auditLog.deviceType,
      userAgent: auditLog.userAgent,
      details: auditLog.details,
      sessionId: auditLog.sessionId,
      requestId: auditLog.requestId,
      responseTime: auditLog.responseTime,
      additionalContext: auditLog.additionalContext
    };

    const blob = new Blob([JSON.stringify(logData, null, 2)], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_log_${auditLog.id}_${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0F2942] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading audit log details...</p>
        </div>
      </div>
    );
  }

  if (!auditLog) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="p-3 bg-red-100 rounded-full mb-4 mx-auto w-fit">
            <AlertTriangle className="h-8 w-8 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Audit Log Not Found</h3>
          <p className="text-gray-600 mb-4">The audit log you're looking for doesn't exist or has been deleted.</p>
          <Button
            variant="primary"
            onClick={handleBack}
            icon={<ArrowLeft className="h-4 w-4" />}
          >
            Back to Audit Logs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={handleBack}
          >
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Audit Log Details</h1>
            <p className="text-gray-600">Audit Log ID: {auditLog.id}</p>
          </div>
        </div>
        <Button
          variant="primary"
          icon={<Download className="h-4 w-4" />}
          onClick={handleExportLog}
        >
          Export Log
        </Button>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-blue-50 rounded-lg">
                {getActionIcon(auditLog.action)}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>
                <p className="text-sm text-gray-600">Core audit log details</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Timestamp</label>
                <div className="flex items-center space-x-2">
                  <Clock className="h-4 w-4 text-gray-400" />
                  <p className="text-gray-900">{formatDate(auditLog.timestamp)}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">User</label>
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-gray-400" />
                  <p className="text-gray-900">{auditLog.user}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Action</label>
                <div className="flex items-center space-x-2">
                  <Activity className="h-4 w-4 text-gray-400" />
                  <p className="text-gray-900">{auditLog.action}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Module</label>
                <div className="flex items-center space-x-2">
                  <FileText className="h-4 w-4 text-gray-400" />
                  <p className="text-gray-900">{auditLog.module}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Status</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(auditLog.status)}`}>
                  {auditLog.status === 'SUCCESS' ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                  {auditLog.status}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Risk Level</label>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRiskColor(auditLog.riskLevel)}`}>
                  {auditLog.riskLevel}
                </span>
              </div>
            </div>
          </div>

          {/* Technical Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-green-50 rounded-lg">
                <Settings className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Technical Details</h2>
                <p className="text-sm text-gray-600">System and network information</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">IP Address</label>
                <p className="text-gray-900 font-mono">{auditLog.ipAddress}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Location</label>
                <div className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4 text-gray-400" />
                  <p className="text-gray-900">{auditLog.location}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Device Type</label>
                <div className="flex items-center space-x-2">
                  {getDeviceIcon(auditLog.deviceType)}
                  <p className="text-gray-900">{auditLog.deviceType}</p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Response Time</label>
                <p className="text-gray-900">{auditLog.responseTime}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Session ID</label>
                <p className="text-gray-900 font-mono text-sm">{auditLog.sessionId}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-2">Request ID</label>
                <p className="text-gray-900 font-mono text-sm">{auditLog.requestId}</p>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-500 mb-2">User Agent</label>
              <p className="text-gray-900 text-sm bg-gray-50 p-3 rounded-lg font-mono break-all">
                {auditLog.userAgent}
              </p>
            </div>
          </div>

          {/* Action Details */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-purple-50 rounded-lg">
                <Edit className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Action Details</h2>
                <p className="text-sm text-gray-600">Specific details about the performed action</p>
              </div>
            </div>

            <div className="space-y-4">
              {Object.entries(auditLog.details).map(([key, value]) => (
                <div key={key} className="flex items-start justify-between py-3 border-b border-gray-100 last:border-b-0">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                    </label>
                    <p className="text-gray-900">
                      {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column - Additional Context & Related Logs */}
        <div className="space-y-6">
          {/* Additional Context */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-orange-50 rounded-lg">
                <Database className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Additional Context</h2>
                <p className="text-sm text-gray-600">User and session context</p>
              </div>
            </div>

            <div className="space-y-4">
              {Object.entries(auditLog.additionalContext).map(([key, value]) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-gray-500 mb-1">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </label>
                  <p className="text-gray-900 text-sm">
                    {key === 'lastLogin' ? formatDate(value) : String(value)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Related Logs */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="p-2 bg-indigo-50 rounded-lg">
                <Activity className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Related Logs</h2>
                <p className="text-sm text-gray-600">Recent activity by the same user</p>
              </div>
            </div>

            <div className="space-y-3">
              {relatedLogs.map((log) => (
                <div key={log.id} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-1 bg-white rounded">
                        {getActionIcon(log.action)}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{log.action}</p>
                        <p className="text-xs text-gray-500">{log.module}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(log.status)}`}>
                        {log.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(log.timestamp, { includeTime: false })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/audit_logs')}
                className="w-full"
              >
                View All Logs
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuditLogDetail;
